import { Typography } from '@mui/material';
import type { ScanJob, BackupJob, RestoreJob } from '@repo/api-gw-sdk';
import { BackupJobType, RestoreJobType, ScanJobType } from '@repo/api-gw-sdk';
import type { Row, Table } from '@tanstack/react-table';

export const restoreJobTypeMapper: Record<RestoreJobType, string> = {
  [RestoreJobType.RestoreJobTypeUnspecified]: 'Restore Job',
  [RestoreJobType.AwsS3ObjectRestore]: 'S3 file restore',
  [RestoreJobType.AwsS3BucketRestore]: 'S3 bucket restore',
  [RestoreJobType.AwsEc2FileRestore]: 'EC2 file restore',
  [RestoreJobType.AwsEc2InstanceRestore]: 'EC2 instance restore',
  [RestoreJobType.AwsEc2AmiConvert]: 'EC2 AMI conversion',
  [RestoreJobType.AwsEc2EbsVolumeRestore]: 'EBS volume restore',
  [RestoreJobType.AwsEc2EbsSnapshotConvert]: 'EBS snapshot conversion',
  [RestoreJobType.AwsEbsVolumeRestore]: 'EBS volume restore',
  [RestoreJobType.AwsEbsSnapshotConvert]: 'EBS snapshot conversion',
  [RestoreJobType.AwsRdsInstanceRestore]: 'RDS instance restore',
  [RestoreJobType.AwsEksNamespaceRestore]: 'EKS Namespace restore',
  [RestoreJobType.AwsEksFileRestore]: 'EKS file restore',
  [RestoreJobType.AwsFsxRestore]: 'FSx restore',
  [RestoreJobType.AwsRedshiftRestore]: 'Redshift restore',
  [RestoreJobType.AwsDocumentdbRestore]: 'DocumentDB restore',
  [RestoreJobType.AwsNeptuneRestore]: 'Neptune restore',
  [RestoreJobType.AwsDynamoDbTableRestore]: 'DynamoDB table restore',
  [RestoreJobType.AwsDynamoDbTableToExistingTableRestore]:
    'DynamoDB restore to existing table',
  [RestoreJobType.AwsEc2RecordRestore]: 'EC2 record restore',
  [RestoreJobType.AwsDynamoDbRecordRestore]: 'DynamoDB record restore',
  [RestoreJobType.AwsKeyspacesTableRestore]: 'Keyspaces table restore',
  [RestoreJobType.AwsKeyspacesRecordRestore]: 'Keyspaces record restore',
  [RestoreJobType.AwsEksNamespaceRecordRestore]: 'EKS Namespace record restore',
  [RestoreJobType.AwsRdsRecordRestore]: 'RDS record restore',
  [RestoreJobType.AzureStorageAccountRestore]: 'Storage account restore',
  [RestoreJobType.AzureStorageAccountBlobRestore]:
    'Storage account file restore',
  [RestoreJobType.AzureVmFileRestore]: 'VM file restore',
  [RestoreJobType.AzureVmDiskRestore]: 'VM disk restore',
  [RestoreJobType.AzureVmInstanceRestore]: 'VM instance restore',
  [RestoreJobType.AzureVmRecordRestore]: 'VM record restore',
  [RestoreJobType.AzureSqlDatabaseRecordRestore]: 'SQL Database record restore',
  [RestoreJobType.AzureSqlDatabaseRestore]: 'SQL Database restore',
  [RestoreJobType.AzureSqlVmInstanceRestore]: 'SQL Virtual Machine restore',
  [RestoreJobType.AzureSqlVmRecordRestore]:
    'SQL Virtual Machine record restore',
  [RestoreJobType.AzurePostgresqlRecordRestore]: 'PostgreSQL record restore',
  [RestoreJobType.AzureSapHanaVmInstanceRestore]: 'SAP HANA VM restore',
  [RestoreJobType.GcpSapHanaVmInstanceRestore]: 'SAP HANA VM restore',
  [RestoreJobType.GcpCloudStorageBucketRestore]: 'GCS bucket restore',
  [RestoreJobType.GcpCloudStorageObjectRestore]: 'GCS file restore',
  [RestoreJobType.GcpComputeEngineFileRestore]: 'GCE file restore',
  [RestoreJobType.GcpComputeEngineDiskRestore]: 'GCE disk restore',
  [RestoreJobType.GcpComputeEngineInstanceRestore]: 'GCE instance restore',
  [RestoreJobType.GcpCloudSqlDatabaseRestore]: 'Cloud SQL instance restore',
  [RestoreJobType.GcpCloudSqlRecordRestore]: 'Cloud SQL record restore',
  [RestoreJobType.GcpComputeEngineRecordRestore]: 'GCE record restore',
  [RestoreJobType.GcpBigqueryDatasetRestore]: 'BigQuery restore',
  [RestoreJobType.GcpBigqueryRecordRestore]: 'BigQuery record restore',
  [RestoreJobType.GcpBigqueryNativeRestoreDataset]: 'BigQuery PITR restore',
  [RestoreJobType.GcpBigqueryNativeRestoreTable]: 'BigQuery PITR restore',
  [RestoreJobType.RdsNativePitrRestore]: 'RDS native PITR restore',
  [RestoreJobType.AtlasClusterRecordRestore]:
    'Mongo Atlas cluster record restore',
  [RestoreJobType.AtlasClusterRestore]: 'Mongo Atlas cluster restore',
};

export const backupJobTypeMapper: Record<BackupJobType, string> = {
  [BackupJobType.BackupJobTypeUnspecified]: 'Backup Job',
  [BackupJobType.AwsEc2Backup]: 'EC2 backup',
  [BackupJobType.AwsEbsVolumeBackup]: 'EBS volume backup',
  [BackupJobType.AwsEc2SnapshotConversion]: 'EC2 snapshot conversion',
  [BackupJobType.AwsRdsBackup]: 'RDS backup',
  [BackupJobType.AwsS3Backup]: 'S3 backup',
  [BackupJobType.AwsS3LlgBackup]: 'Log space ingestion',
  [BackupJobType.AwsFsxBackup]: 'FSx backup',
  [BackupJobType.AwsEfsBackup]: 'EFS backup',
  [BackupJobType.AwsRedshiftBackup]: 'Redshift backup',
  [BackupJobType.AwsDocumentdbBackup]: 'DocumentDB backup',
  [BackupJobType.AwsNeptuneBackup]: 'Neptune backup',
  [BackupJobType.AwsDynamoDbBackup]: 'DynamoDB backup',
  [BackupJobType.AwsDynamoDbBackupFull]: 'DynamoDB full scan',
  [BackupJobType.AwsEksNamespaceBackup]: 'EKS Namespace backup',
  [BackupJobType.AzureStorageAccountBackup]: 'Storage account backup',
  [BackupJobType.AzureVmBackup]: 'VM backup',
  [BackupJobType.AzureVmRestorePointConversion]: 'VM backup conversion',
  [BackupJobType.AzureSqlVirtualMachineBackup]: 'SQL Virtual Machine backup',
  [BackupJobType.AzureSqlVirtualMachineBackupFull]:
    'SQL Virtual Machine full backup',
  [BackupJobType.AzureSqlDatabaseBackup]: 'SQL database backup',
  [BackupJobType.AzureSqlManagedInstanceBackup]: 'SQL managed instance backup',
  [BackupJobType.AzurePostgresqlBackup]: 'PostgreSQL backup',
  [BackupJobType.AzureMysqlBackup]: 'MySQL backup',
  [BackupJobType.GcpComputeEngineInstanceBackup]: 'GCE instance backup',
  [BackupJobType.GcpDiskBackup]: 'GCE disk backup',
  [BackupJobType.GcpCloudStorageBackup]: 'GCS backup',
  [BackupJobType.GcpCloudSqlBackup]: 'Cloud SQL backup',
  [BackupJobType.GcpBigqueryBackup]: 'BigQuery backup',
  [BackupJobType.GcpSapHanaVmBackup]: 'SAP HANA VM backup',
  [BackupJobType.AzureSapHanaVmBackup]: 'SAP HANA VM backup',
  [BackupJobType.AtlasMongodbClusterBackup]: 'MongoDB cluster backup',
};

export const scanJobTypeMapper: Record<ScanJobType, string> = {
  [ScanJobType.ScanJobTypeUnspecified]: 'Classification Job',
  [ScanJobType.AwsEc2Scan]: 'EC2 classification',
  [ScanJobType.AwsEbsVolumeScan]: 'EBS volume classification',
  [ScanJobType.AwsRdsScan]: 'RDS classification',
  [ScanJobType.AwsS3Scan]: 'S3 classification',
  [ScanJobType.AwsDynamoDbScan]: 'DynamoDB classification',
  [ScanJobType.AwsEksNamespaceScan]: 'EKS Namespace classification',
  [ScanJobType.AzureStorageAccountScan]: 'Storage account classification',
  [ScanJobType.AzureVmScan]: 'VM classification',
  [ScanJobType.AzureSqlDatabaseScan]: 'SQL database classification',
  [ScanJobType.AzureSqlVirtualMachineScan]:
    'SQL Virtual Machine classification',
  [ScanJobType.GcpComputeEngineInstanceScan]: 'GCE instance classification',
  [ScanJobType.GcpDiskScan]: 'GCE disk classification',
  [ScanJobType.GcpCloudStorageScan]: 'GCS classification',
  [ScanJobType.GcpCloudSqlScan]: 'Cloud SQL classification',
  [ScanJobType.GcpBigqueryScan]: 'BigQuery classification',
  [ScanJobType.AtlasMongodbClusterScan]: 'MongoDB cluster classification',
};

export const getJobType = (entity: BackupJob | RestoreJob | ScanJob) => {
  if ('restoreType' in entity) {
    return !entity.restoreType
      ? 'Restore Job'
      : restoreJobTypeMapper[entity.restoreType];
  }
  if ('backupType' in entity) {
    return !entity.backupType
      ? 'Backup Job'
      : backupJobTypeMapper[entity.backupType];
  }
  if ('scanType' in entity) {
    return !entity.scanType
      ? 'Classification Job'
      : scanJobTypeMapper[entity.scanType];
  }
  return;
};

export const JobTypeCell = ({
  row,
  table,
}: {
  row: Row<BackupJob | RestoreJob | ScanJob>;
  table: Table<BackupJob | RestoreJob | ScanJob>;
}) => {
  const job = row.original;

  const toggleJobsCard = () => {
    if (row.getIsSelected()) {
      table.resetRowSelection(true);
    } else {
      table.setRowSelection({
        [row.id]: true,
      });
    }
  };

  const jobType = getJobType(job);
  return (
    jobType && (
      <Typography
        className='underline hover:cursor-pointer hover:text-[var(--mui-palette-text-secondary)]'
        onClick={toggleJobsCard}
      >
        {jobType}
      </Typography>
    )
  );
};
